// Import and re-export centralized enums
import {
  ActivityType,
  ActivitySectionType,
  BlockType,
  ResourceType,
} from "./types/enums";
export type { ReadingVariant } from "./types/enums";

export { ActivityType, ActivitySectionType, BlockType, ResourceType };

// Application-specific enums
export enum Role {
  ADMIN = "ADMIN",
  STUDENT = "STUDENT",
  TEACHER = "TEACHER",
  COORDINATOR = "COORDINATOR",
}

export enum EventTitle {
  USER_CHANGE_PASSWORD = "USER_CHANGE_PASSWORD",
}

export enum CourseLevel {
  KINDER = "KINDER",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
}

// Interfaces
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  role: Role;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  school_id: number;
  school?: School;
  score?: Score;
  events?: Event[];
  classrooms?: Classroom[];
}

export interface School {
  id: number;
  cct: string;
  name: string;
  city: string;
  demo: boolean;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  users?: User[];
  classrooms?: Classroom[];
}

export interface Score {
  id: number;
  main_score: number;
  user_id: number;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  user?: User;
}

export interface Event {
  id: number;
  title: EventTitle;
  user_id: number;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  user?: User;
}

export interface Classroom {
  id: number;
  name: string;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  school_id: number;
  school?: School;
  users?: User[];
  subject_id: number;
  subject?: Subject;
  grade_id: number;
  grade?: Grade;
}

export interface Subject {
  id: number;
  code: string;
  name: string;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  classrooms?: Classroom[];
  programs?: Program[];
  courses?: Course[];
}

export interface Program {
  id: number;
  title: string;
  description: string;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  subject_id: number;
  subject?: Subject;
  grade_id: number;
  grade?: Grade;
  programToCourse?: ProgramToCourse[];
}

export interface Grade {
  id: number;
  grade: string;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  classrooms?: Classroom[];
  programs?: Program[];
  courses?: Course[];
}

export interface ProgramToCourse {
  course_id: number;
  course?: Course;
  program_id: number;
  program?: Program;
  order_by: number;
  week: number;
  pages: string;
  trimester: number;
}

export interface Course {
  id: number;
  title: string;
  content: string;
  pda: string;
  learning_goals: string;
  level: CourseLevel;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  subject_id: number;
  subject?: Subject;
  grade_id: number;
  grade?: Grade;
  tags?: Tag[];
  courseToLesson?: CourseToLesson[];
  programToCourse?: ProgramToCourse[];
}

export interface Tag {
  id: number;
  name: string;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  courses?: Course[];
}

export interface CourseToLesson {
  course_id: number;
  course?: Course;
  lesson_id: number;
  lesson?: Lesson;
  created_at: Date;
  order_by: number;
}

export interface Lesson {
  id: number;
  title: string;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  courseToLesson?: CourseToLesson[];
  lessonToActivity?: LessonToActivity[];
}

export interface LessonToActivity {
  lesson_id: number;
  lesson?: Lesson;
  activity_id: number;
  activity?: Activity;
  order_by: number;
  activityType: ActivityType;
}

export interface Activity {
  id: number;
  title: string;
  duration: number;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  blocks?: Block[];
  lessonToActivity?: LessonToActivity[];
}

export interface Block {
  id: number;
  type: BlockType;
  order_by: number;
  text?: string | null;
  image?: string | null;
  url?: string | null;
  resource?: ResourceType | null;
  created_at: Date;
  updated_at: Date | null;
  deleted_at: Date | null;
  activity_id: number;
  activity?: Activity;
}

export interface CreateLessonDto {
  title: string;
}

// Si necesitas crear una lección y asociarla con cursos al mismo tiempo
export interface CreateLessonWithCoursesDto {
  title: string;
  courses: {
    course_id: number;
    order_by: number;
  }[];
}

// Si necesitas crear una lección y asociarla con actividades al mismo tiempo
export interface CreateLessonWithActivitiesDto {
  title: string;
  activities: {
    activity_id: number;
    order_by: number;
    activityType: "START" | "MIDDLE" | "CLOSING";
  }[];
}

// Tipo completo que incluye todas las posibles relaciones
export interface CreateLessonCompleteDto {
  title: string;
  courses?: {
    course_id: number;
    order_by: number;
  }[];
  activities?: {
    activity_id: number;
    order_by: number;
    activityType: "START" | "MIDDLE" | "CLOSING";
  }[];
}
